import { useEffect, useState } from "react";
import "./new-school.css";
import InputSelect from "../../../../ui/atoms/inputs/input-select/InputSelect";
import Loading from "../../../../ui/atoms/loading/Loading";
import MessageBox from "../../../../ui/atoms/message-box/MessageBox";
import InputTextArea from "../../../../ui/atoms/inputs/input-textarea/InputTextArea";
import Button from "../../../../ui/atoms/buttons/Button";
import { useTranslation } from "react-i18next";
//
import { onGetSchools, onGetStreams } from "../../../admin/services/school";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { wait, isEmpty, capitalizeAll } from "../../../../core/utils/helpers";
import { NewRegisterSchema } from "../../../../core/utils/validators/authentication";
import { onNewSchoolInscription } from "../../../authenticate/services/authentication";
import Swal from "sweetalert2";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";

const NewSchool = ({ user_id, myInscriptions, setIsRefresh }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();

  const [isSending, setIsSending] = useState(false);
  const [isShowingMsgBox, setIsShowingMsgBox] = useState(false);
  const [messageData, setMessageData] = useState({ type: "", text: "" });

  const [schoolsData, setSchoolsData] = useState([]);
  const [streamsData, setStreamsData] = useState([]);

  const [schoolsArray, setSchoolsArray] = useState([]);
  const [streamsArray, setStreamsArray] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      let isMounted = true;
      const controller = new AbortController();
      const signal = controller.signal;

      await onGetSchools(axiosPrivate, signal).then((response) => {
        const _data = response?.data?.result || [];
        setSchoolsData(_data);
      });

      await onGetStreams(axiosPrivate, signal).then((response) => {
        const _data = response?.data?.result || [];
        setStreamsData(_data);
      });

      return () => {
        isMounted = false;
        isMounted && controller.abort();
      };
    };
    fetchData();
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(NewRegisterSchema),
    defaultValues: {
      user_id: user_id,
      program_language: "",
    },
  });

  const [selectedProgramLanguage, school] = watch([
    "program_language",
    "school",
  ]);

  useEffect(() => {
    const onLoading = async () => {
      Swal.fire({
        title: "Loading Schools...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      //
      await wait(1000);
      //
      const tmpArray = schoolsData?.filter(
        (el) =>
          el?.school_is_activated &&
          el?.school_is_deleted === false &&
          el?.school_language === selectedProgramLanguage,
      );

      const _avalaibleSchools = Array.from(tmpArray, (item, _) => ({
        key: item?.school_id,
        title: capitalizeAll(item?.school_title),
        is_activated: false,
      }));

      setSchoolsArray(_avalaibleSchools);
      Swal.close();
    };
    if (!isEmpty(selectedProgramLanguage)) onLoading();
  }, [selectedProgramLanguage]);

  useEffect(() => {
    const onLoading = async () => {
      Swal.fire({
        title: "Loading Streams...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      //
      await wait(1000);
      //
      const tmpArray = streamsData?.filter(
        (item) =>
          item?.school_id === school &&
          item?.classe_is_activated === true &&
          item?.classe_is_deleted === false,
      );

      const _avalaibleStreams = Array.from(tmpArray, (item, _) => ({
        key: item?.classe_id,
        title: capitalizeAll(item?.classe_title),
        is_activated: false,
      }));
      //
      // Process in order to remove existing streams
      const normalize = (str) => str.toLowerCase().trim();

      const streamTitlesSet = new Set(
        myInscriptions.map((item) => normalize(item.stream_title)),
      );

      const _result = _avalaibleStreams.filter(
        (item) => !streamTitlesSet.has(normalize(item.title)),
      );

      setStreamsArray(_result);
      Swal.close();
    };
    if (!isEmpty(school)) onLoading();
  }, [school]);

  const onSubmit = async (data) => {
    setIsSending(true);
    await wait(700);
    //
    onNewSchoolInscription(axiosPrivate, data)
      .then((response) => {
        setIsSending(false);
        //
        const _data = response?.data;
        if (_data?.status) {
          setIsShowingMsgBox(true);
          setMessageData({ type: "success", text: _data?.message });
          setIsRefresh((prev) => !prev);
          reset();

          const timer = setTimeout(() => {
            setIsShowingMsgBox(false);
            setMessageData({ type: "", text: "" });
          }, 2000);
          return () => clearTimeout(timer);
        } else {
          setIsShowingMsgBox(true);
          setMessageData({ type: "warning", text: _data?.message });
          //
          const timer = setTimeout(() => {
            setIsShowingMsgBox(false);
            setMessageData({ type: "", text: "" });
          }, 3000);
          return () => clearTimeout(timer);
        }
      })
      .catch((error) => {
        setIsSending(false);
        setIsShowingMsgBox(true);
        if (!error?.response) {
          setMessageData({ type: "info", text: "No server response" });
        } else {
          setMessageData({
            type: "warning",
            text: error?.response?.data?.detail?.message,
          });
        }
        const timer = setTimeout(() => {
          setIsShowingMsgBox(false);
          setMessageData({ type: "", text: "" });
        }, 2000);
        return () => clearTimeout(timer);
      });
  };

  return (
    <form className="new-school" onSubmit={handleSubmit(onSubmit)}>
      {isShowingMsgBox && (
        <div className="w-full mb-xs">
          <MessageBox type={messageData?.type} msg={messageData?.text} />
        </div>
      )}
      <div className="w-full mb-md">
        <InputSelect
          register={register}
          name={"program_language"}
          placeholder={t("user.program_language")}
          data={[
            {
              key: "en",
              title: "English",
              is_activated: false,
            },
            {
              key: "fr",
              title: "French",
              is_activated: false,
            },
          ]}
          error={errors?.program_language?.message}
        />
      </div>
      <div className="w-full mb-md">
        <InputSelect
          register={register}
          name={"school"}
          placeholder={"School of Interest"}
          data={schoolsArray}
          error={errors?.school?.message}
        />
      </div>
      <div className="w-full mb-md">
        <InputSelect
          register={register}
          name={"stream"}
          placeholder={"Stream (Class) of Interest"}
          data={streamsArray}
          error={errors?.stream?.message}
        />
      </div>
      <div className="w-full mb-md">
        <InputTextArea
          type={"text"}
          register={register}
          name="expectations"
          title={t("user.expectations")}
          error={errors?.expectations?.message}
        />
      </div>
      <div className="w-full">
        {isSending ? (
          <Loading type={"type-1"} />
        ) : (
          <Button
            type={"submit"}
            preferedStyle={"normal"}
            title={"Get Inscription"}
            action={null}
          />
        )}
      </div>
    </form>
  );
};

export default NewSchool;
